import * as Toast from "@radix-ui/react-toast";
import { Pencil } from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

interface OwnerEditDialogProps {
  id: number;
  currentName: string;
  currentTelephone: string;
  currentCellPhone: string;
  currentEmail: string;
  currentObservation: string;
  onOwnerUpdate: () => void;
}

export function OwnerEditDialog({
  id,
  currentName,
  currentTelephone,
  currentCellPhone,
  currentEmail,
  currentObservation,
  onOwnerUpdate,
}: OwnerEditDialogProps) {
  const [openModel, setOpenModel] = useState(false);
  const [name, setName] = useState(currentName);
  const [telephone, setTelephone] = useState(currentTelephone);
  const [cellPhone, setCellPhone] = useState(currentCellPhone);
  const [email, setEmail] = useState(currentEmail);
  const [observation, setObservation] = useState(currentObservation);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(false);

  function handleToOpenModel() {
    setOpenModel(true);
    setName(currentName);
    setTelephone(currentTelephone);
    setCellPhone(currentCellPhone);
    setEmail(currentEmail);
    setObservation(currentObservation);
  }

  function formatPhoneNumber(value: string): string {
    value = value.replace(/\D/g, "");
    if (value.length <= 2) return `(${value}`;
    else if (value.length <= 6)
      return `(${value.slice(0, 2)}) ${value.slice(2)}`;
    else if (value.length <= 10)
      return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
    else
      return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
  }

  async function handleSaveChanges() {
    if (!name.trim()) {
      setToastType("error");
      setToastMessage("O nome do proprietário é obrigatório!");
      setToastOpen(true);
      return;
    }
    setLoading(true);
    const updatedOwner = {
      data: {
        name,
        telephone,
        cell_phone: cellPhone,
        email,
        observation,
      },
    };
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      const response = await fetch(`${route}/api/owners/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedOwner),
      });
      if (!response.ok) throw new Error("Falha ao atualizar o proprietário");
      setOpenModel(false);
      onOwnerUpdate();
      setToastType("success");
      setToastMessage("Proprietário editado com sucesso!");
      setToastOpen(true);
    } catch (error) {
      setToastOpen(true);
      setToastType("error");
      setToastMessage("Falha ao atualizar o proprietário!");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Toast.Provider swipeDirection="right">
        <Dialog open={openModel} onOpenChange={setOpenModel}>
          <DialogTrigger asChild>
            <Button
              onClick={handleToOpenModel}
              className="rounded-full bg-gray-100 p-2 hover:bg-gray-200"
              title="Editar proprietário"
            >
              <Pencil className="text-gray-700" />
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-xl bg-white p-6 shadow-xl sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                Editar Proprietário
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Após editar, basta clicar em SALVAR.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="name" className="mb-1 block">
                  Nome <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome do proprietário"
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="telephone" className="mb-1 block">
                  Telefone
                </Label>
                <Input
                  id="telephone"
                  value={telephone}
                  onChange={(e) =>
                    setTelephone(formatPhoneNumber(e.target.value))
                  }
                  placeholder="(99) 9999-9999"
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="cellPhone" className="mb-1 block">
                  Celular
                </Label>
                <Input
                  id="cellPhone"
                  value={cellPhone}
                  onChange={(e) =>
                    setCellPhone(formatPhoneNumber(e.target.value))
                  }
                  placeholder="(99) 99999-9999"
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="email" className="mb-1 block">
                  Email
                </Label>
                <Input
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  className="w-full"
                  disabled={loading}
                />
              </div>
              <div>
                <Label htmlFor="observation" className="mb-1 block">
                  Observação
                </Label>
                <Textarea
                  id="observation"
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  placeholder="Observações sobre o proprietário"
                  className="w-full"
                  disabled={loading}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={handleSaveChanges}
                disabled={loading}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {loading ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Toast.Root
          open={toastOpen}
          onOpenChange={setToastOpen}
          className={`fixed bottom-0 right-0 m-6 rounded-md p-4 shadow-md ${
            toastType === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <Toast.Description>{toastMessage}</Toast.Description>
          <Toast.Close className="absolute right-1 top-1" />
        </Toast.Root>
        <Toast.Viewport />
      </Toast.Provider>
    </div>
  );
}
